// Turning a stored SectionStyle into CSS. Shared by the public renderer and the
// Form Builder canvas so a styled section or card looks identical in both.
import type { SectionStyle } from '../shared/types';
import { gradientCss } from '../shared/types';

/**
 * The `background` value for a style: a solid colour, or a two-stop gradient.
 *
 * A section's unnamed direction means VERTICAL — that is what this drew
 * before one could be chosen, and `sanitizeStyle` writes the same default — so
 * the fall-through is spelled here rather than left to `gradientCss`, whose
 * own default (diagonal) belongs to the brand.
 */
export function backgroundOf(style: SectionStyle | undefined): string | undefined {
  const bg = style?.bg;
  if (!bg || bg === 'transparent') return undefined;
  if (!style?.bg2) return bg;
  return gradientCss(bg, style.bg2, style.gradient || 'vertical');
}

/**
 * The padding an instance card of a repeatable container gets once its own
 * background replaces the one its class paints — the value that class already
 * used at that depth, so styling a container changes its colour, not its
 * density. Shared with the builder canvas, which draws the same boxes.
 */
export const instancePadding = (depth: number): number => (depth === 0 ? 24 : depth === 1 ? 20 : 16);

export interface BoxStyleOptions {
  /**
   * Padding applied when the box has its own background (sections use more).
   * `false` = the box's own class already pads it at every width, so nothing
   * inline is written — an inline value would beat the phone's media query and
   * leave a styled box padded like a desktop one.
   */
  padding?: number | false;
}

/** Inline styles for a section or card box. */
export function boxStyle(style: SectionStyle | undefined, opts: BoxStyleOptions = {}): React.CSSProperties {
  const out: React.CSSProperties = {};
  const bg = backgroundOf(style);
  if (bg) {
    out.background = bg;
    // The theme's border, like every other box on the form draws — a dark
    // theme must not ring a styled section in a light grey line.
    out.border = '2px solid var(--of-border, #e2e8f0)';
    out.borderRadius = style?.radius || 0;
    if (opts.padding !== false) out.padding = opts.padding ?? 24;
  } else if (style?.radius) {
    out.borderRadius = style.radius;
  }
  // An authored width narrower than the form (the builder offers 75% and 50%)
  // never exceeds the column it is in; the cap is here so a width that ever
  // could — a stored px value from an older draft — cannot push a phone's
  // page sideways. On a desktop it changes nothing.
  if (style?.width && style.width !== '100%') { out.width = style.width; out.maxWidth = '100%'; }
  if (style?.height) out.minHeight = Number(style.height);
  return out;
}

/*
 * A section is drawn in two halves, because a background belongs on the box
 * that holds the questions — not on the wrapper that also holds the heading.
 * That is how a repeatable container has always read (colour on the instance
 * card, title above it on the page), and a section now reads the same way: one
 * box, the fields sitting directly on the colour, no second well inside it.
 */

/** The section wrapper: an authored width still narrows heading and body alike. */
export function sectionWrapStyle(style: SectionStyle | undefined): React.CSSProperties {
  const out: React.CSSProperties = {};
  if (style?.width && style.width !== '100%') { out.width = style.width; out.maxWidth = '100%'; }
  // A min height with no background has no box of its own to stretch, so it
  // stretches the section, exactly as it always has.
  if (style?.height && !backgroundOf(style)) out.minHeight = Number(style.height);
  return out;
}

/** The section BODY — `.of-section-card`, and what a background paints. */
export function sectionBodyStyle(style: SectionStyle | undefined): React.CSSProperties {
  const out = boxStyle(style, { padding: false });
  // Width is the wrapper's business: the body fills the column it is given.
  delete out.width; delete out.maxWidth;
  return out;
}

/** Does this section carry a background of its own? */
export const hasBackground = (style: SectionStyle | undefined): boolean => !!backgroundOf(style);
